/**
 * Timeline Repository Queries
 *
 * Server-side aggregation for efficient timeline rendering of 6000+ documents.
 * Uses PostgreSQL RPC functions for bucketed counts and paginated windows.
 */

import { supabase } from "@/integrations/supabase/client";
import { hydrateLinkedIssues, profileDisplayName } from "./mappers";
import { getDocTimestamp } from "./documentDates";
import type {
  TimelineSummaryParams,
  TimelineWindowParams,
  TimelineSummaryResult,
  TimelineWindowResult,
  TimelineBucket,
  TimelineGranularity,
  TimelineFilterParams,
} from "./timeline-types";
import { TimelineDateBasisUnavailableError } from "./timeline-types";
import type { DocumentRowMapped } from "./types";

/**
 * Map raw RPC document row to DocumentRowMapped.
 * RPC returns raw documents without joins, so we provide defaults for joined fields.
 * Tags and created_by_name are enriched separately via fetchDocumentTags.
 */
function mapRpcDocumentRow(doc: any): DocumentRowMapped {
  return {
    id: doc.id,
    filename: doc.filename,
    file_path: doc.file_path,
    building_id: doc.building_id,
    parent_document_id: doc.parent_document_id,
    content_type: doc.content_type,
    size: doc.size,
    created_at: doc.created_at,
    original_created_at: doc.original_created_at,
    modified_at: doc.modified_at,
    document_content_modified_date: doc.document_content_modified_date,
    effective_modified_at: doc.effective_modified_at ?? null,
    created_by: doc.created_by,
    created_by_name: null, // Enriched by fetchDocumentTags
    created_by_avatar_url: null, // Enriched by fetchDocumentTags
    created_by_color: null, // Enriched by fetchDocumentTags
    state: doc.state,
    version: doc.version,
    version_group: doc.version_group,
    // The timeline RPCs return none of these three; the logo reads the
    // building's Franklin-made ids instead of `source`.
    source: doc.source ?? null,
    is_latest_version: doc.is_latest_version ?? null,
    container_type: doc.container_type ?? null,
    project_id: doc.project_id,
    folder_id: doc.folder_id,
    is_deleted: doc.is_deleted,
    categories: [], // Enriched by fetchDocumentTags
    design_disciplines: [], // Enriched by fetchDocumentTags
    design_stages: [], // Enriched by fetchDocumentTags
    approved: doc.approved ?? false,
    verified_at: doc.verified_at ?? null,
    verified_by: doc.verified_by ?? null,
    verified_by_name: null, // Enriched by fetchDocumentEnrichments
    linked_issues: [], // WHA-3270 — hydrated by hydrateLinkedIssues
  };
}

/**
 * Per-document data the timeline RPC can't return, resolved in one batch.
 *
 * Declared once at module scope: the signature and the map it builds used to
 * be two separate inline literals and had already drifted (`created_by_color`
 * was missing from the signature).
 */
type EnrichmentData = {
  categories: { id: number; name: string; name_et: string | null }[];
  design_disciplines: {
    id: number;
    name: string;
    name_en: string | null;
    abbreviation: string | null;
    description: string | null;
  }[];
  design_stages: {
    id: number;
    name: string;
    name_en: string | null;
    abbreviation: string | null;
  }[];
  created_by_name: string | null;
  created_by_avatar_url: string | null;
  created_by_color: string | null;
  verified_by_name: string | null;
};

/**
 * Fetch tags (categories, disciplines, stages), the uploader's profile and the
 * verifier's name for a batch of documents.
 * Returns a map of document_id -> enrichment data.
 */
async function fetchDocumentEnrichments(
  documentIds: number[]
): Promise<Map<number, EnrichmentData>> {
  if (documentIds.length === 0) return new Map();

  // Paginated fetch helper for M2M junction tables.
  // While enrichment batches are typically small (50 docs), the junction rows could
  // exceed PostgREST's max_rows = 1000 if documents have many M2M relations.
  async function fetchPaginatedM2M<T>(
    table: string,
    selectColumns: string,
    filterIds: number[]
  ): Promise<{ data: T[] | null; error: { message: string } | null }> {
    const PAGE_SIZE = 1000;
    const allData: T[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from(table)
        .select(selectColumns)
        .in("document_id", filterIds)
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) return { data: null, error };
      const page = (data ?? []) as T[];
      allData.push(...page);

      if (page.length < PAGE_SIZE) {
        hasMore = false;
      } else {
        offset += PAGE_SIZE;
      }
    }

    return { data: allData, error: null };
  }

  // Fetch all related data in parallel with error handling for graceful degradation.
  // If any query fails, we log the error but continue - documents will display without
  // the failed enrichment data (e.g., without tags or created_by_name).
  const [categoriesResult, disciplinesResult, stagesResult, profilesResult] = await Promise.all([
    // Categories (paginated)
    fetchPaginatedM2M<{
      document_id: number;
      document_categories: { id: number; category_name: string; name_et: string | null };
    }>(
      "document_document_categories",
      "document_id, document_categories(id, category_name, name_et)",
      documentIds
    ),
    // Disciplines (paginated)
    fetchPaginatedM2M<{
      document_id: number;
      design_disciplines: {
        id: number;
        name: string;
        name_en: string | null;
        abbreviation: string | null;
        description: string | null;
      };
    }>(
      "document_design_disciplines",
      "document_id, design_disciplines(id, name, name_en, abbreviation, description)",
      documentIds
    ),
    // Stages (paginated)
    fetchPaginatedM2M<{
      document_id: number;
      design_stages: {
        id: number;
        name: string;
        name_en: string | null;
        abbreviation: string | null;
      };
    }>(
      "document_design_stages",
      "document_id, design_stages(id, name, name_en, abbreviation)",
      documentIds
    ),
    // User profiles for created_by_name - fetch via documents table join
    supabase
      .from("documents")
      .select(
        "id, user_profiles!documents_created_by_fk(first_name, last_name, avatar_url, color), " +
          "verified_by_profile:user_profiles!documents_verified_by_fk(first_name, last_name)"
      )
      .in("id", documentIds),
  ]);

  // Log any errors but continue with graceful degradation
  if (categoriesResult.error) {
    console.error("Failed to fetch document categories:", categoriesResult.error);
  }
  if (disciplinesResult.error) {
    console.error("Failed to fetch document disciplines:", disciplinesResult.error);
  }
  if (stagesResult.error) {
    console.error("Failed to fetch document stages:", stagesResult.error);
  }
  if (profilesResult.error) {
    console.error("Failed to fetch user profiles:", profilesResult.error);
  }

  const enrichmentMap = new Map<number, EnrichmentData>();

  // Initialize empty entries for all document IDs
  for (const id of documentIds) {
    enrichmentMap.set(id, {
      categories: [],
      design_disciplines: [],
      design_stages: [],
      created_by_name: null,
      created_by_avatar_url: null,
      created_by_color: null,
      verified_by_name: null,
    });
  }

  // Process categories
  if (categoriesResult.data) {
    for (const row of categoriesResult.data) {
      const entry = enrichmentMap.get(row.document_id);
      if (entry && row.document_categories) {
        const cat = row.document_categories as {
          id: number;
          category_name: string;
          name_et: string | null;
        };
        entry.categories.push({
          id: cat.id,
          name: cat.category_name,
          name_et: cat.name_et ?? null,
        });
      }
    }
  }

  // Process disciplines
  if (disciplinesResult.data) {
    for (const row of disciplinesResult.data) {
      const entry = enrichmentMap.get(row.document_id);
      if (entry && row.design_disciplines) {
        const disc = row.design_disciplines as {
          id: number;
          name: string;
          name_en: string | null;
          abbreviation: string | null;
          description: string | null;
        };
        entry.design_disciplines.push({
          id: disc.id,
          name: disc.name,
          name_en: disc.name_en ?? null,
          abbreviation: disc.abbreviation,
          description: disc.description,
        });
      }
    }
  }

  // Process stages
  if (stagesResult.data) {
    for (const row of stagesResult.data) {
      const entry = enrichmentMap.get(row.document_id);
      if (entry && row.design_stages) {
        const stage = row.design_stages as {
          id: number;
          name: string;
          name_en: string | null;
          abbreviation: string | null;
        };
        entry.design_stages.push({
          id: stage.id,
          name: stage.name,
          name_en: stage.name_en ?? null,
          abbreviation: stage.abbreviation,
        });
      }
    }
  }

  // Process user profiles for created_by_name
  if (profilesResult.data) {
    for (const row of profilesResult.data) {
      const entry = enrichmentMap.get(row.id);
      if (entry && row.user_profiles) {
        const profile = row.user_profiles as {
          first_name: string | null;
          last_name: string | null;
          avatar_url: string | null;
          color: string | null;
        };
        if (profile.first_name || profile.last_name) {
          entry.created_by_name = [profile.first_name, profile.last_name].filter(Boolean).join(" ");
        }
        entry.created_by_avatar_url = profile.avatar_url ?? null;
        entry.created_by_color = profile.color ?? null;
      }
      if (entry) {
        entry.verified_by_name = profileDisplayName(
          row.verified_by_profile as { first_name: string | null; last_name: string | null } | null
        );
      }
    }
  }

  return enrichmentMap;
}

/**
 * Enrich documents with tags and created_by_name.
 * Mutates the documents array in place.
 */
async function enrichDocumentsWithTags(documents: DocumentRowMapped[]): Promise<void> {
  if (documents.length === 0) return;

  const documentIds = documents.map((d) => d.id);
  const enrichments = await fetchDocumentEnrichments(documentIds);

  for (const doc of documents) {
    const enrichment = enrichments.get(doc.id);
    if (enrichment) {
      doc.categories = enrichment.categories;
      doc.design_disciplines = enrichment.design_disciplines;
      doc.design_stages = enrichment.design_stages;
      doc.created_by_name = enrichment.created_by_name;
      doc.created_by_avatar_url = enrichment.created_by_avatar_url;
      doc.created_by_color = enrichment.created_by_color;
      doc.verified_by_name = enrichment.verified_by_name;
    }
  }
}

/**
 * Convert frontend folderId to RPC format.
 * - undefined → null (no filter)
 * - null → -1 (root folder, folder_id IS NULL)
 * - string → number (specific folder)
 */
function convertFolderId(folderId: string | null | undefined): number | null {
  if (folderId === undefined) return null; // No filter
  if (folderId === null) return -1; // Root folder (NULL in DB)
  return Number(folderId);
}

/** PostgREST / Postgres "no function with these arguments". */
const MISSING_FUNCTION_CODES: ReadonlySet<string> = new Set(["PGRST202", "42883"]);

/**
 * The date-basis argument for both timeline RPCs. Only the upload basis is
 * sent: the file basis is each RPC's default, so leaving it out keeps the
 * Timeline working against a database that predates `p_date_basis`.
 */
function dateBasisArg(params: TimelineFilterParams): { p_date_basis?: "upload" } {
  return params.dateBasis === "upload" ? { p_date_basis: "upload" } : {};
}

/**
 * The added-date range for both timeline RPCs (pipeline #377). Sent only when a
 * bound is set, so a Timeline without the filter keeps working against a
 * database that predates the parameters.
 */
function addedRangeArgs(params: TimelineFilterParams): {
  p_created_from?: string;
  p_created_to?: string;
} {
  const { from, to } = params.uploadDateRange ?? {};
  return {
    ...(from ? { p_created_from: from } : {}),
    ...(to ? { p_created_to: to } : {}),
  };
}

/**
 * An upload-basis call the database cannot resolve — the RPCs have no
 * `p_date_basis` yet. Anything else is a real failure and is left alone.
 */
function dateBasisUnavailable(
  params: TimelineFilterParams,
  error: { code?: string } | null
): TimelineDateBasisUnavailableError | null {
  if (params.dateBasis !== "upload" || !error?.code) return null;
  return MISSING_FUNCTION_CODES.has(error.code) ? new TimelineDateBasisUnavailableError() : null;
}

/**
 * Fetch aggregated timeline summary (bucketed counts).
 * Used for zoomed-out view to show clusters without loading all docs.
 *
 * @param params - Building ID, granularity, and optional filters
 * @returns Bucketed counts with time ranges
 */
export async function fetchTimelineSummary(
  params: TimelineSummaryParams
): Promise<TimelineSummaryResult> {
  const { data, error } = await supabase.rpc("get_document_timeline_summary", {
    p_building_id: Number(params.buildingId),
    p_granularity: params.granularity,
    // Filter parameters
    p_project_ids: params.projectIds ?? null,
    p_category_ids: params.categoryIds ?? null,
    p_discipline_ids: params.designDisciplineIds ?? null,
    p_stage_ids: params.designStageIds ?? null,
    p_search_text: params.searchText ?? null,
    p_folder_id: convertFolderId(params.folderId),
    p_verified_only: params.verifiedOnly ?? false,
    ...dateBasisArg(params),
    ...addedRangeArgs(params),
  });

  if (error) throw dateBasisUnavailable(params, error) ?? new Error(error.message);

  const buckets: TimelineBucket[] = (data || []).map((row: any) => ({
    bucketStart: new Date(row.bucket_start),
    bucketEnd: new Date(row.bucket_end),
    documentCount: row.document_count,
    earliestDocId: row.earliest_doc_id,
    latestDocId: row.latest_doc_id,
  }));

  const totalDocuments = buckets.reduce((sum, b) => sum + b.documentCount, 0);

  // Buckets are ordered DESC, so last = oldest, first = newest
  const timeRange =
    buckets.length > 0
      ? {
          start: buckets[buckets.length - 1].bucketStart,
          end: buckets[0].bucketEnd,
        }
      : { start: new Date(), end: new Date() };

  return { buckets, totalDocuments, timeRange };
}

/**
 * Two of `get_documents_in_time_window`'s 22023s mean "your cursor is stale,
 * restart the window": the cursor row was hard-deleted mid-page, or its
 * `effective_modified_at` was re-stamped out of the window (franklin-data-pipeline
 * `20260730210810_*` / `20260831144525_*`). Its other 22023s are our own call
 * being wrong — `p_limit` out of range, a null or inverted window, half a cursor
 * pair — and retrying those only costs a round trip and then reports page one's
 * failure in place of the real cause.
 *
 * SQLSTATE alone cannot separate the two groups: every one of them is 22023,
 * raised by the same function. So the discriminator is the PARAMETER the message
 * names, not the sentence around it — `p_cursor_id` alone is a stale cursor,
 * while the half-a-pair error names `p_cursor_timestamp` as well. A parameter
 * name is the RPC's signature, which no caller can survive a rename of anyway;
 * the prose is free to be reworded in the other repo without breaking recovery
 * here. `__tests__/fetchDocumentsInTimeWindow.test.ts` pins both the current
 * wordings and a reworded one.
 */
function isStaleCursorError(error: { code?: string; message: string }): boolean {
  if (error.code !== "22023" || !/\bp_cursor_id\b/.test(error.message)) return false;
  return !/\bp_cursor_timestamp\b/.test(error.message);
}

/**
 * `get_documents_in_time_window`'s own ceiling on `p_limit` (1..500, raised as a
 * 22023 by franklin-data-pipeline `20260730203825`). Asking for more is a failed
 * round-trip, not a bigger page.
 */
const MAX_WINDOW_PAGE = 500;

/**
 * Re-page a restarted window back to the depth the caller was looking at.
 *
 * A restart answers with page one, but the caller may be scrolled through
 * several pages of an expanded cluster. Replacing what it holds with 50 rows
 * collapses the list under the user mid-scroll, so walk forward until at least
 * `params.loadedRows` rows are back (or the window runs out).
 *
 * Cursor paging cannot be issued concurrently — page n+1's cursor is page n's
 * last row — so the refill asks for the biggest page the RPC allows instead of
 * repeating the caller's page size. A max-zoom list 2000 rows deep costs 4
 * round trips (and 4 enrichment batches) rather than 40, and 500 ids per
 * enrichment `.in()` is the batch size the repository already uses elsewhere
 * (`IN_CHUNK_SIZE`).
 */
async function refillRestartedWindow(
  params: TimelineWindowParams,
  firstPage: TimelineWindowResult
): Promise<TimelineWindowResult> {
  const target = params.loadedRows ?? 0;
  let acc = firstPage;
  const seen = new Set(acc.documents.map((d) => d.id));

  while (acc.documents.length < target && acc.hasMore && acc.cursor) {
    // `loadedRows: 0`: a page rejected in its own turn restarts once and stops
    // there, rather than re-entering this loop from inside itself.
    const next = await fetchDocumentsInTimeWindow({
      ...params,
      cursor: acc.cursor,
      limit: refillPageSize(params, target - acc.documents.length),
      loadedRows: 0,
    });
    const folded = foldRefillPage(acc, next, seen);
    // The cursor we just sent was rejected, and it is the cursor the caller
    // would store and send again on its next scroll — every one of which would
    // pay for another full-depth restart. Close the window at the depth we did
    // recover instead, and report THAT as its total: the server's count covers
    // rows this window can no longer page to, so keeping it renders a count the
    // user can never scroll to. The rows are not lost — collapsing and
    // re-expanding the cluster reads the window again from a fresh cursor, and
    // brings the full count back with it. `truncated` is what says so: without
    // it the caller reads this as a window that simply ended, and the list stops
    // mid-cluster with no way to ask for the rest.
    if (!folded) {
      return {
        ...acc,
        totalCount: acc.documents.length,
        hasMore: false,
        cursor: null,
        truncated: true,
      };
    }
    acc = folded;
  }

  return acc;
}

/**
 * The page to ask the RPC for, given how many rows are still outstanding.
 * Never below the caller's own page size: overshooting the target by part of one
 * page is what it asked for anyway, and a `p_limit` of 0 is a 22023 of its own.
 */
function refillPageSize(params: TimelineWindowParams, remaining: number): number {
  return Math.min(MAX_WINDOW_PAGE, Math.max(params.limit ?? 50, remaining));
}

/**
 * Fold one refill page into the rows recovered so far, or `null` when the page
 * was itself a restart — a second stale cursor hands back page one again, and
 * re-sending that cursor only spins the loop.
 *
 * A page that repeats rows we already hold is NOT a dead end: its own cursor
 * points past those rows, so adopting it steps over the repeat rather than
 * stalling on it. Paging still terminates — a non-empty page always moves the
 * cursor forward, and an empty one comes back `has_more: false`.
 * `seen` is updated in place.
 */
function foldRefillPage(
  acc: TimelineWindowResult,
  next: TimelineWindowResult,
  seen: Set<DocumentRowMapped["id"]>
): TimelineWindowResult | null {
  // A cursor that did not move is the one dead end left: re-sending it can only
  // fetch the same page again.
  if (next.restarted || (next.cursor && acc.cursor && next.cursor.id === acc.cursor.id)) {
    return null;
  }
  const fresh = next.documents.filter((d) => !seen.has(d.id));
  for (const doc of fresh) seen.add(doc.id);
  return {
    documents: fresh.length > 0 ? [...acc.documents, ...fresh] : acc.documents,
    totalCount: next.totalCount,
    hasMore: next.hasMore,
    cursor: next.cursor,
  };
}

/**
 * Fetch documents for a time window with cursor-based pagination.
 * Used when expanding a cluster to show actual documents.
 *
 * @param params - Building ID, time range, limit, and optional cursor
 * @returns Paginated documents with metadata
 */
export async function fetchDocumentsInTimeWindow(
  params: TimelineWindowParams
): Promise<TimelineWindowResult> {
  const { data, error } = await supabase.rpc("get_documents_in_time_window", {
    p_building_id: Number(params.buildingId),
    p_start_time: params.startTime.toISOString(),
    p_end_time: params.endTime.toISOString(),
    p_limit: params.limit ?? 50,
    p_cursor_timestamp: params.cursor?.timestamp?.toISOString() ?? null,
    p_cursor_id: params.cursor?.id ?? null,
    // Filter parameters
    p_project_ids: params.projectIds ?? null,
    p_category_ids: params.categoryIds ?? null,
    p_discipline_ids: params.designDisciplineIds ?? null,
    p_stage_ids: params.designStageIds ?? null,
    p_search_text: params.searchText ?? null,
    p_folder_id: convertFolderId(params.folderId),
    p_verified_only: params.verifiedOnly ?? false,
    ...dateBasisArg(params),
    ...addedRangeArgs(params),
  });

  if (error) {
    const unavailable = dateBasisUnavailable(params, error);
    if (unavailable) throw unavailable;
    // A stale cursor: the RPC reads the sort key off the cursor ROW, and that
    // row is either gone — hard-deleted between two pages — or its
    // `effective_modified_at` has moved out of the window (both raised by
    // franklin-data-pipeline 20260730210810 / 20260831144525, which chose a 400
    // the caller can act on over silently serving a truncated page). The window
    // is still valid, only the cursor is stale, so re-read it from the first
    // page — as deep as the caller had it, see `loadedRows` — and tell the
    // caller, which must replace what it holds rather than append the re-read
    // rows to it. Throwing instead lands as an unhandled rejection —
    // every load-more caller is a void callback — and infinite scroll stops dead
    // with nothing on screen to say why.
    //
    // Matched on the parameter the message names, not the bare SQLSTATE: the
    // same 22023 also covers an out-of-range p_limit, a null or inverted window
    // and half a cursor pair, and those are OUR call being wrong. Restarting
    // them buys a second failing round-trip and then reports page one's error in
    // place of the real one. See isStaleCursorError.
    if (params.cursor && isStaleCursorError({ code: error.code, message: error.message ?? "" })) {
      // `p_cursor_id does not name a row in this building` is also what a cursor
      // built from the WRONG building or window looks like, and that is our bug,
      // not a stale row. The RPC cannot tell them apart — both are "no such row
      // here" — so the restart stays, and the restart is reported instead: a
      // recovery that leaves no trace is a recovery that hides a real defect
      // behind behaviour indistinguishable from normal paging.
      console.warn("Timeline cursor rejected, restarting the window from page one", {
        buildingId: params.buildingId,
        cursorId: params.cursor.id,
        window: [params.startTime.toISOString(), params.endTime.toISOString()],
        reason: error.message,
      });
      const restart = await fetchDocumentsInTimeWindow({
        ...params,
        cursor: undefined,
        loadedRows: 0,
      });
      // Page one alone would truncate a caller scrolled past it, so hand back
      // the depth it had (see refillRestartedWindow).
      return { ...(await refillRestartedWindow(params, restart)), restarted: true };
    }
    throw new Error(error.message);
  }

  const rows = data || [];

  // RPC returns rows with {doc, total_count, has_more}
  const documents = rows.map((row: any) => mapRpcDocumentRow(row.doc));
  const totalCount = rows[0]?.total_count ?? 0;
  const hasMore = rows.length > 0 && rows[rows.length - 1]?.has_more === true;

  // Enrich documents with tags and created_by_name (not available from RPC)
  await enrichDocumentsWithTags(documents);
  // WHA-3270 — batch-hydrate the PM issue chip data for the row renderers.
  await hydrateLinkedIssues(documents);

  // Build cursor for next page from last document. `effective_modified_at` (or
  // `created_at` on the upload basis) is the column
  // get_documents_in_time_window orders on, so this is the page
  // boundary rather than an approximation of it — the RPC still recomputes the
  // sort key from `p_cursor_id` (a `Date` here is millisecond-truncated, and
  // the column is stamped after the row is visible, so it can move under an
  // open pager) and rejects the call with SQLSTATE 22023 if that row has left
  // the window. `getDocTimestamp` keeps the pre-20260831 fallback chain for
  // rows fetched without the column.
  const lastDoc = documents[documents.length - 1];
  const cursor =
    lastDoc && hasMore
      ? { timestamp: getDocTimestamp(lastDoc, params.dateBasis), id: lastDoc.id }
      : null;

  const page: TimelineWindowResult = { documents, totalCount, hasMore, cursor };

  // A cursorless call that still names a depth is a caller re-reading a window
  // it already holds rows of — the recovery a `truncated` window is offered,
  // where the cursor is the thing that failed. Page one alone would collapse
  // the list it is replacing, so page forward exactly as a restart does.
  if (!params.cursor && (params.loadedRows ?? 0) > documents.length) {
    return refillRestartedWindow(params, page);
  }

  return page;
}

/**
 * Map zoom level (0-100) to appropriate granularity.
 * Lower zoom = larger buckets, higher zoom = smaller buckets.
 *
 * @param zoomLevel - Current zoom level (0-100)
 * @returns Granularity for server-side bucketing
 */
export function zoomToGranularity(zoomLevel: number): TimelineGranularity {
  if (zoomLevel < 25) return "year";
  if (zoomLevel < 50) return "month";
  if (zoomLevel < 75) return "week";
  return "day";
}

/**
 * Fetch ALL documents for a building with cursor-based pagination.
 * Used for max zoom mode where we show individual documents (not buckets).
 * Documents are sorted by timestamp DESC (newest first).
 *
 * @param buildingId - Building to fetch documents for
 * @param limit - Max documents per page
 * @param cursor - Cursor for pagination (timestamp + id of last fetched doc)
 * @param filters - Optional filters (project, categories, disciplines, stages, search, folder)
 * @returns Paginated documents with metadata
 */
export async function fetchAllTimelineDocuments(
  buildingId: string,
  limit = 50,
  cursor?: { timestamp: Date; id: number },
  filters?: TimelineFilterParams,
  /** Rows the caller holds; only used to restore depth after a stale cursor. */
  loadedRows = 0
): Promise<TimelineWindowResult> {
  // Use a very wide time range to get all documents
  const farPast = new Date("1900-01-01T00:00:00Z");
  const farFuture = new Date("2100-01-01T00:00:00Z");

  return fetchDocumentsInTimeWindow({
    buildingId,
    startTime: farPast,
    endTime: farFuture,
    limit,
    cursor,
    loadedRows,
    // Pass through filters
    ...filters,
  });
}
